import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { ProgressBar, Button } from 'react-bootstrap';

import ReactGA4 from 'react-ga4';

import { getFile } from '../../../../util/file.js';

import { getVouchers, checkVoucherConditions, applyVoucher } from '../../VoucherActions.js';
import { getOrdersByUser } from '../../../Order/OrderActions.js';
import { getCart, getCartVouchers } from '../../../Cart/CartActions.js';
import { getCartTotals } from '../../../Cart/CartHelpers.js';
import { memoizedGetProducts } from '../../../Product/ProductActions.js';
import { getLoggedUser } from '../../../User/UserActions.js';
import { getUserGroup } from '../../../User/UserGroupActions.js';

import Price from '../../../Currency/components/view/Price.js';
import HtmlComponent from '../../../../components/Text/HtmlComponent.js';
import GamifiedButton from '../../../../components/Content/GamifiedButton.js';
import config from '../../../../config.js';
import classNames from 'classnames';

function VoucherProgress({ dispatch, user = null, userGroup = null, cart = null, cartTotals = null, cartVouchers = [], vouchers = [], products = [], orders = [], onCart = false, hideProgress = false, isStandalone = false, ctaAnchor = null }) {
    const [displayConditions, setDisplayConditions] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
		setMounted(true);
    }, []);

	if(!mounted) {
		return null;
	}

	if(cart.items.find(item => (item.flags || []).includes('fidelity'))) {
		return null;
	}

    const vouchersSelected = (vouchers || [])
        .filter(voucher => voucher.isPublic || voucher.isAuto)
        .concat(cartVouchers)
        .sort((voucherA, voucherB) => {
            if(voucherA.priority || voucherB.priority) {
                return (voucherA.priority || 0) - (voucherB.priority || 0);
            }
            const getMinSubtotalCondition = voucher => voucher.conditions.filter(condition => condition.key === 'subtotal').reduce((min, condition) => Math.min(condition.value, min || condition.value), null);
            return getMinSubtotalCondition(voucherA) - getMinSubtotalCondition(voucherB);
        });
    const nextVoucher = cart.isVoucherPrioritary ? (cartVouchers || [])[0] : vouchersSelected.find(voucher => voucher.conditions.find(condition => condition.key === 'subtotal' && condition.value > cartTotals.items));

	const handleClickCTA = () => {
		if(nextVoucher) {
			dispatch(applyVoucher(nextVoucher));
			if(typeof window !== 'undefined' && config.google.analytics.trackingCodeGA4) {
				// Ga: New
				const data = {
					creative_name: ctaAnchor,
					creative_slot: 'voucher_progress',
					promotion_id: nextVoucher?.code,
					promotion_name: nextVoucher?.name,
				};
				console.log('GA: Select promotion', data);
				ReactGA4.event('select_promotion', data);
			}
		}
	};

    // if(cartVouchers.length) {
    //     [nextVoucher] = cartVouchers;
    // }
    // console.log(vouchersSelected, nextVoucher);

    if(nextVoucher && checkVoucherConditions({ ...nextVoucher, conditions: nextVoucher.conditions.filter(condition => condition.key !== 'subtotal') }, user, userGroup, orders, cart)) {
        const nextVoucherStep = nextVoucher.conditions.filter(condition => condition.key === 'subtotal').reduce((step, condition) => Math.max(condition.value, step), 0);
        const nextVoucherProductAction = nextVoucher.actions.find(action => action.key === 'product');
        const offerProduct = nextVoucherProductAction && products.find(product => product._id === nextVoucherProductAction.value);
        const offerPicture = offerProduct && getFile(((offerProduct.pictures || []).find(picture => picture.includes('jpg'))), { width: 100 });
        return (
            <div className={classNames(!isStandalone && ['border', 'rounded-3', 'p-2'], 'bg-white')}>
                <div className="d-block d-md-flex align-items-center gap-3 p-2">
					<div className="d-none d-md-block">
						{
							!onCart && offerPicture
							? <img src={offerPicture} alt="" role="presentation" className={classNames('rounded-3', 'flex-shrink-0', displayConditions && ['d-none', 'd-md-inline'])} width={displayConditions ? 90 : 48} height={displayConditions ? 90 : 48} style={{ objectFit: 'cover' }} loading="lazy" />
							: <span className="fs-4 flex-shrink-0" role="img" aria-label="cadeau">🎁</span>
						}
					</div>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="small lh-sm">
							<span className="fw-bold text-dark ">
								{nextVoucher.name}
								{nextVoucherStep > 0 && (
									nextVoucherStep > cartTotals.items
									? <span className="text-danger"> — <FormattedMessage id="voucherProductOfferThreshold" defaultMessage="Only {amount} left to take advantage of the offer!" values={{ amount: <Price amount={nextVoucherStep - cartTotals.items} /> }} /></span>
									: <span className="text-success"> — 🎉 <FormattedMessage id="voucherProductOfferSuccess" defaultMessage="Great ! You get the offer!" /></span>
								)}
								{(!displayConditions && (cartTotals.items?.length > 0 || nextVoucherStep > 0)) && <em className="d-block text-start small" role="button" tabIndex={0} onClick={setDisplayConditions} onKeyDown={e => e.key === 'Enter' && setDisplayConditions(true)}>{' '}<FormattedMessage id="learnMoreButton" defaultMessage="Learn more" /></em>}
							</span>

							{(displayConditions || cartTotals.items?.length === 0 || nextVoucherStep === 0) && (
								<div className="mt-1 ps-2 rounded small" style={{ /* borderLeft: '4px solid var(--bs-danger)' */ }}>
									<HtmlComponent text={nextVoucher.description} />
								</div>
							)}
                        </div>
                        {!hideProgress && nextVoucherStep > 0 && (
                            <ProgressBar
                                variant="danger"
                                now={Math.min(100, (cartTotals.items * 100) / nextVoucherStep)}
                                className="rounded-pill bg-info mt-1"
                                style={{ height: '7px' }}
                            />
                        )}
                    </div>
                    {ctaAnchor && (
                        <GamifiedButton as={Button} variant="danger" size="sm" className="flex-shrink-0 rounded-pill mt-1 py-1 px-2" tag="a" href={ctaAnchor} onClick={handleClickCTA}>
                            <FormattedMessage id="voucherButtonCTA" defaultMessage="Take advantage of the offer" /> 🎁
                        </GamifiedButton>
                    )}
                </div>
            </div>
        );
    }
	if(process.env.NODE_ENV === 'development') {
		return <span>{nextVoucher?.label}: conditions {!checkVoucherConditions(nextVoucher, user, userGroup, orders, cart) && 'not'} checked</span>;
	}
    return null;
}

function mapStateToProps(store, props) {
    const user = getLoggedUser(store);
    return {
        user,
		userGroup: getUserGroup(store),
        cart: getCart(store),
        cartTotals: getCartTotals(store),
        cartVouchers: getCartVouchers(store),
        vouchers: getVouchers(store, true).filter(voucher => checkVoucherConditions({ ...voucher, conditions: voucher.conditions.filter(condition => condition.key !== 'subtotal') }, user, getUserGroup(store), user && getOrdersByUser(store, user._id, ['onpreparation', 'shipped', 'complete']), getCart(store))),
        products: memoizedGetProducts(store, { isActive: true }),
		orders: user ? getOrdersByUser(store, user._id) : [],
    };
}

VoucherProgress.propTypes = {
    dispatch: PropTypes.func.isRequired,
	user: PropTypes.object,
	userGroup: PropTypes.object,
    cart: PropTypes.object,
    cartTotals: PropTypes.object,
    cartVouchers: PropTypes.arrayOf(PropTypes.object),
    vouchers: PropTypes.arrayOf(PropTypes.object),
    products: PropTypes.arrayOf(PropTypes.object),
	orders: PropTypes.arrayOf(PropTypes.object),
	onCart: PropTypes.bool,
	hideProgress: PropTypes.bool,
	isStandalone: PropTypes.bool,
	ctaAnchor: PropTypes.string,
};

export default connect(mapStateToProps)(VoucherProgress);
